// File: frontend/src/components/Calendar/AppointmentDetailsPopup.js
// Version: 3.0 (quick-260915-kal — зашитые цвета убраны, цвет держит CSS)
// Description: Executive Legal Style details modal matching oslawdash (manus.space).
// Displays full Google Calendar event information: Client Name, Topic, Time, Meet link, Description.

import React from 'react';
import { format } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useUiLanguage } from '../../context/UiLanguageContext';
import { 
    X, 
    Calendar as CalendarIcon, 
    Clock, 
    Video, 
    User, 
    Mail, 
    Phone, 
    FileText, 
    ExternalLink 
} from 'lucide-react';
import './AppointmentDetailsPopup.css';

const AppointmentDetailsPopup = ({ isOpen, onClose, event, eventData, onEventUpdated }) => {
    const { t, lang } = useUiLanguage();
    const isEn = lang !== 'ru';
    const dateLocale = isEn ? enUS : ru;
    const data = eventData || event;
    if (!isOpen || !data) return null;

    const title = data.summary || data.title || t('cal.modal.untitled');
    const startTime = data.start?.dateTime || data.start?.date || data.start;
    const endTime = data.end?.dateTime || data.end?.date || data.end;
    const googleMeetLink = data.hangoutLink || data.raw?.hangoutLink;
    const googleCalendarLink = data.htmlLink || data.raw?.htmlLink;
    const description = data.description || data.raw?.description || '';

    // Extract lead or client details
    const leadDetails = data.lead_details || data.raw?.lead_details;
    let clientName = data.clientName || data.client_name || leadDetails?.full_name || '';
    let clientEmail = leadDetails?.email || '';
    let clientPhone = leadDetails?.phone || '';
    let topic = leadDetails?.topic || '';

    // If description has formatted text like "Client: ... Phone: ... Topic: ...", parse it
    if (description) {
        const lines = description.split('\n');
        for (const line of lines) {
            const l = line.trim();
            if (l.toLowerCase().startsWith('client:') && !clientName) {
                clientName = l.substring(7).trim();
            } else if (l.toLowerCase().startsWith('email:') && !clientEmail) {
                clientEmail = l.substring(6).trim();
            } else if (l.toLowerCase().startsWith('phone:') && !clientPhone) {
                clientPhone = l.substring(6).trim();
            } else if (l.toLowerCase().startsWith('topic:') && !topic) {
                topic = l.substring(6).trim();
            }
        }
    }

    // Fallback if title has format "Consultation: Ekaterina Sukhina"
    if (!clientName && title.includes(':')) {
        clientName = title.split(':')[1].trim();
    }

    const formatDateTimeRange = (startVal, endVal) => {
        if (!startVal) return '';
        try {
            const startD = typeof startVal === 'string' ? new Date(startVal) : startVal;
            const endD = endVal ? (typeof endVal === 'string' ? new Date(endVal) : endVal) : null;
            if (isEn) {
                const datePart = format(startD, 'EEEE, MMMM d, yyyy', { locale: dateLocale });
                if (!endD) {
                    return `${datePart} · ${format(startD, 'h:mm a')}`;
                }
                const startAmPm = format(startD, 'a');
                const endAmPm = format(endD, 'a');
                const startTimeStr = startAmPm === endAmPm ? format(startD, 'h:mm') : format(startD, 'h:mm a');
                const endTimeStr = format(endD, 'h:mm a');
                return `${datePart} · ${startTimeStr} – ${endTimeStr}`;
            } else {
                const datePart = format(startD, 'EEEE, d MMMM yyyy · HH:mm', { locale: dateLocale });
                if (!endD) return datePart;
                return `${datePart} – ${format(endD, 'HH:mm')}`;
            }
        } catch {
            return String(startVal);
        }
    };

    return (
        <div className="cal-modal-overlay" onClick={onClose}>
            <div className="cal-modal-container" onClick={(e) => e.stopPropagation()}>
                
                {/* Modal Header */}
                <div className="cal-modal-header">
                    <div>
                        <span className="cal-modal-tag">{t('cal.modal.tag')}</span>
                        <h3 className="cal-modal-title">{title}</h3>
                    </div>
                    <button type="button" className="cal-modal-close-btn" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="cal-modal-body">
                    
                    {/* Date & Time Row */}
                    <div className="cal-modal-info-row">
                        <div className="cal-modal-icon-col">
                            <Clock size={16} className="cal-ink-gold" />
                        </div>
                        <div className="cal-modal-text-col">
                            <div className="cal-modal-label">{t('cal.modal.date_time')}</div>
                            <div className="cal-modal-val font-semibold">
                                {formatDateTimeRange(startTime, endTime)}
                            </div>
                        </div>
                    </div>

                    {/* Client Information Card */}
                    <div className="cal-modal-client-card">
                        <div className="cal-modal-client-head">
                            <User size={15} />
                            <span>{t('cal.modal.client_info')}</span>
                        </div>
                        <div className="cal-modal-client-grid">
                            <div className="cal-modal-client-field">
                                <span className="cal-field-lbl">{t('cal.modal.client')}</span>
                                <span className="cal-field-val font-bold">{clientName || t('cal.modal.not_specified')}</span>
                            </div>
                            {topic && (
                                <div className="cal-modal-client-field">
                                    <span className="cal-field-lbl">{t('cal.modal.topic')}</span>
                                    <span className="cal-field-val font-semibold">{topic}</span>
                                </div>
                            )}
                            {clientPhone && (
                                <div className="cal-modal-client-field">
                                    <span className="cal-field-lbl">{t('cal.modal.phone')}</span>
                                    <a href={`tel:${clientPhone}`} className="cal-field-link">
                                        <Phone size={12} className="inline mr-1" />
                                        {clientPhone}
                                    </a>
                                </div>
                            )}
                            {clientEmail && (
                                <div className="cal-modal-client-field">
                                    <span className="cal-field-lbl">{t('cal.modal.email')}</span>
                                    <a href={`mailto:${clientEmail}`} className="cal-field-link">
                                        <Mail size={12} className="inline mr-1" />
                                        {clientEmail}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Google Meet Link if exists */}
                    {googleMeetLink && (
                        <div className="cal-modal-meet-box">
                            <div className="cal-meet-left">
                                <Video size={18} className="cal-ink-green" />
                                <div>
                                    <div className="font-bold text-xs text-foreground">{t('cal.modal.meet_title')}</div>
                                    <div className="text-[11px] cal-ink-muted">{t('cal.modal.meet_desc')}</div>
                                </div>
                            </div>
                            <a 
                                href={googleMeetLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="cal-meet-btn"
                            >
                                {t('cal.modal.meet_join')}
                                <ExternalLink size={12} />
                            </a>
                        </div>
                    )}

                    {/* Description Notes if exist */}
                    {description && (
                        <div className="cal-modal-notes-box">
                            <div className="cal-notes-label">
                                <FileText size={14} />
                                <span>{t('cal.modal.notes')}</span>
                            </div>
                            <pre className="cal-notes-text">{description}</pre>
                        </div>
                    )}

                    {/* External Google Calendar Link */}
                    {googleCalendarLink && (
                        <div className="cal-modal-footer-link">
                            <a 
                                href={googleCalendarLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="cal-gcal-ext-link"
                            >
                                <CalendarIcon size={14} />
                                <span>{t('cal.modal.open_gcal')}</span>
                                <ExternalLink size={12} />
                            </a>
                        </div>
                    )}

                </div>

                {/* Modal Footer */}
                <div className="cal-modal-footer">
                    <button type="button" className="cal-modal-btn-close" onClick={onClose}>
                        {t('cal.modal.close')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AppointmentDetailsPopup;
